// src/pages/StudentPortal.jsx
// Redesigned student dashboard - ONE route, client-side section switching.
// Same architecture as Pilomation's employee dashboard (ManagementDashboard.jsx):
// a single parent holds activeSection state, sidebar <li> items call
// setActiveSection(...), main content conditionally renders the matching
// section component. No React Router sub-routes for these sections.
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FiHome, FiFolder, FiTool, FiCheckSquare, FiUsers, FiStar, FiMessageCircle,
  FiHelpCircle, FiChevronRight, FiMenu, FiX,
} from 'react-icons/fi';
import { AnimatePresence, motion } from 'framer-motion';
import SubNav from '../components/subNav';
import DashboardHome from '../components/studentPortal/DashboardHome';
import MyProjects from '../components/studentPortal/MyProjects';
import Tools from '../components/studentPortal/Tools';
import Community from '../components/studentPortal/Community';
import TasksAndCertificates from '../components/studentPortal/TasksAndCertificates';
import AlumniNetwork from '../components/studentPortal/AlumniNetwork';
import TalentShowcase from '../components/studentPortal/TalentShowcase';

const SECTIONS = [
  { key: 'dashboard', label: 'Dashboard', icon: FiHome },
  { key: 'projects', label: 'My Projects', icon: FiFolder },
  { key: 'tools', label: 'Tools', icon: FiTool },
  { key: 'tasks', label: 'Tasks and Certificates', icon: FiCheckSquare },
  { key: 'alumni', label: 'Alumni Network', icon: FiUsers },
  { key: 'showcase', label: 'Talent Showcase', icon: FiStar },
  { key: 'community', label: 'Community', icon: FiMessageCircle },
];

const StudentPortal = () => {
  const [activeSection, setActiveSection] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const activeLabel = SECTIONS.find((s) => s.key === activeSection)?.label;

  const renderSection = () => {
    switch (activeSection) {
      case 'dashboard':
        return <DashboardHome onNavigate={setActiveSection} />;
      case 'projects':
        return <MyProjects />;
      case 'tools':
        return <Tools />;
      case 'tasks':
        return <TasksAndCertificates />;
      case 'alumni':
        return <AlumniNetwork />;
      case 'showcase':
        return <TalentShowcase />;
      case 'community':
        return <Community />;
      default:
        return null;
    }
  };

  const goTo = (key) => {
    setActiveSection(key);
    setSidebarOpen(false);
  };

  return (
    <div className="flex flex-col min-h-screen bg-slate-50">
      {/* Existing app-wide top nav (profile, feedback, home, logout) - messenger
          hidden here specifically, since messaging isn't part of this program.
          It's a normal, non-fixed 61px-tall bar (40px logo + 10px+10px padding
          + 1px border), so everything below it needs to sit below that exact
          offset instead of the viewport's true top:0. */}
      <SubNav hideMessenger />

      <div className="flex flex-1 relative">
        {/* Mobile top bar - sits below SubNav (61px), not at the viewport's top:0 */}
        <div className="lg:hidden fixed top-[61px] left-0 right-0 z-30 flex items-center justify-between bg-white border-b border-slate-200 px-4 py-3">
          <span className="font-bold text-slate-800">Student Portal</span>
          <button onClick={() => setSidebarOpen((v) => !v)} className="text-slate-600">
            {sidebarOpen ? <FiX size={22} /> : <FiMenu size={22} />}
          </button>
        </div>

        {sidebarOpen && (
          <div className="lg:hidden fixed inset-0 bg-black/40 z-30" onClick={() => setSidebarOpen(false)} />
        )}

        <aside
          className={`w-64 shrink-0 bg-white border-r border-slate-200 flex flex-col fixed top-[61px] h-[calc(100vh-61px)] lg:sticky lg:top-0 lg:h-[calc(100vh-61px)] z-40 transition-transform duration-300 ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
          }`}
        >
          <div className="p-5 border-b border-slate-100 hidden lg:block">
            <h1 className="text-lg font-bold text-slate-800">Student Portal</h1>
          </div>
          <div className="lg:hidden h-14" />

          <nav className="flex-1 overflow-y-auto px-3 py-4">
            <ul className="space-y-1.5">
              {SECTIONS.map(({ key, label, icon: Icon }) => (
                <li
                  key={key}
                  onClick={() => goTo(key)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium cursor-pointer transition-all duration-200 ${
                    activeSection === key
                      ? 'bg-teal-50 text-teal-700'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-800 hover:translate-x-0.5'
                  }`}
                >
                  <Icon size={18} />
                  <span>{label}</span>
                </li>
              ))}
            </ul>
          </nav>

          <div className="p-3 border-t border-slate-100">
            <Link
              to="/contactus"
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-500 hover:bg-slate-50 hover:text-teal-600 hover:translate-x-0.5 transition-all duration-200"
            >
              <FiHelpCircle size={18} />
              <span>Need help? Contact us</span>
            </Link>
          </div>
        </aside>

        <main className="flex-1 p-6 md:p-8 pt-[118px] lg:pt-8 max-w-[1400px] mx-auto w-full">
          <div className="flex items-center gap-1.5 text-sm text-slate-500 mb-5">
            <button
              onClick={() => setActiveSection('dashboard')}
              className={`font-medium transition-colors duration-150 ${
                activeSection === 'dashboard' ? 'text-slate-800' : 'hover:text-teal-600 hover:underline'
              }`}
            >
              Dashboard
            </button>
            {activeSection !== 'dashboard' && (
              <>
                <FiChevronRight size={12} />
                <span className="font-medium text-slate-800">{activeLabel}</span>
              </>
            )}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeSection}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
            >
              {renderSection()}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
};

export default StudentPortal;
